import type { ReactNode } from 'react';

export function Field({
  label,
  hint,
  children,
  required = false,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  required?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1 text-sm font-medium text-foreground-700">
        {label}
        {required && <span className="text-rose-500">*</span>}
      </label>
      {hint && <p className="text-xs text-foreground-400">{hint}</p>}
      {children}
    </div>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-accent-300"
    />
  );
}

export function TextArea({
  value,
  onChange,
  placeholder,
  rows = 3,
  maxLength = 500,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      maxLength={maxLength}
      className="w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-accent-300 resize-none"
    />
  );
}

export function ScorePicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm text-foreground-700">{label}</span>
        <span className="text-sm font-medium text-accent-600">{value === null ? '—' : value}</span>
      </div>
      <div className="flex gap-1.5">
        {Array.from({ length: 11 }, (_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onChange(i)}
            className={`flex-1 h-9 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
              value === i
                ? 'bg-accent-500 text-background-50'
                : 'bg-background-100 border border-foreground-200 text-foreground-500 hover:border-foreground-300'
            }`}
          >
            {i}
          </button>
        ))}
      </div>
      <div className="flex justify-between mt-1 text-[10px] text-foreground-400">
        <span>無不適</span>
        <span>最嚴重</span>
      </div>
    </div>
  );
}

export function ChipGroup({
  options,
  values,
  onChange,
  multiple = true,
}: {
  options: string[];
  values: string[];
  onChange: (v: string[]) => void;
  multiple?: boolean;
}) {
  const toggle = (opt: string) => {
    if (multiple) {
      onChange(values.includes(opt) ? values.filter((v) => v !== opt) : [...values, opt]);
    } else {
      onChange([opt]);
    }
  };

  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const active = values.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors cursor-pointer whitespace-nowrap ${
              active
                ? 'bg-accent-100 border-accent-300 text-accent-800'
                : 'bg-background-50 border-foreground-200 text-foreground-500 hover:border-foreground-300'
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex items-start gap-3 w-full text-left cursor-pointer group"
    >
      <span
        className={`mt-0.5 w-10 h-6 rounded-full p-0.5 transition-colors shrink-0 ${
          checked ? 'bg-accent-500' : 'bg-foreground-200'
        }`}
      >
        <span
          className={`block w-5 h-5 rounded-full bg-white transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </span>
      <span className="text-sm text-foreground-700 leading-snug group-hover:text-foreground-900 transition-colors">
        {label}
      </span>
    </button>
  );
}

export function StepBadge({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-foreground-500">
        第 {current} 步 / 共 {total} 步
      </span>
      <div className="flex gap-1">
        {Array.from({ length: total }, (_, i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all ${
              i + 1 === current ? 'w-5 bg-accent-500' : i + 1 < current ? 'w-1.5 bg-accent-300' : 'w-1.5 bg-foreground-200'
            }`}
          />
        ))}
      </div>
    </div>
  );
}